// Wave 20.38a — Session replay controls.
//
// Video-player-style widget for scrubbing along a recorded session:
// play / pause, dynamic speed toggle, progress bar, current-time
// readout, optional fullscreen toggle. Stateless — all state lives
// in the parent (ActivityDetail); this component just renders the
// UI and dispatches events.
//
// Pair with the useReplayClock hook below to drive the state.

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { colors } from '../theme';

// Wave 20.38c — speed is now a number, not a fixed enum. Default
// (Auto) is computed to make total playback ~15 s regardless of
// how long the session actually was; user can dial down from
// there. Compute the four choices as [1x, ¼auto, ½auto, Auto].
export type ReplaySpeed = number;

/** Total wall-clock seconds we target for a full session replay
 *  when the speed picker is on Auto. */
export const REPLAY_TARGET_WALL_SEC = 15;

/** Compute the Auto speed: number of session-seconds per one
 *  wall-second of playback. A 30-min run at target 15 s → 120x. */
export function computeAutoSpeed(durationSec: number): number {
  if (!durationSec || durationSec <= 0) return 1;
  return Math.max(1, Math.round(durationSec / REPLAY_TARGET_WALL_SEC));
}

/** Speed picker choices — always ends with Auto. */
export function replaySpeedChoices(autoSpeed: number): Array<{ label: string; value: number }> {
  const s = Math.max(1, autoSpeed);
  const half = Math.max(1, Math.round(s / 2));
  const quarter = Math.max(1, Math.round(s / 4));
  const opts: Array<{ label: string; value: number }> = [
    { label: '1x', value: 1 },
  ];
  if (quarter > 1 && quarter !== half) opts.push({ label: `${quarter}x`, value: quarter });
  if (half > 1 && half !== s) opts.push({ label: `${half}x`, value: half });
  opts.push({ label: `Auto (${s}x)`, value: s });
  return opts;
}

interface Props {
  durationSec: number;
  positionSec: number;
  playing: boolean;
  speed: ReplaySpeed;
  autoSpeed: number;
  onPlayPause: () => void;
  onSeek: (sec: number) => void;
  onSpeedChange: (s: ReplaySpeed) => void;
  /** When defined, shows a fullscreen toggle. Passing a callback
   *  means the caller wants the button visible. */
  onToggleFullscreen?: () => void;
  fullscreen?: boolean;
}

function formatClock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

export default function SessionReplayControls({
  durationSec,
  positionSec,
  playing,
  speed,
  autoSpeed,
  onPlayPause,
  onSeek,
  onSpeedChange,
  onToggleFullscreen,
  fullscreen,
}: Props) {
  const atEnd = positionSec >= durationSec - 0.1;
  const handlePlayPause = useCallback(() => {
    if (atEnd) onSeek(0);
    onPlayPause();
  }, [atEnd, onPlayPause, onSeek]);
  const speedChoices = useMemo(() => replaySpeedChoices(autoSpeed), [autoSpeed]);

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 2,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          size="small"
          onClick={handlePlayPause}
          sx={{ color: colors.brand }}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {atEnd && !playing ? (
            <ReplayIcon fontSize="small" />
          ) : playing ? (
            <PauseIcon fontSize="small" />
          ) : (
            <PlayArrowIcon fontSize="small" />
          )}
        </IconButton>
        <Typography
          sx={{
            color: colors.textMuted,
            fontSize: '0.72rem',
            fontFamily: '"JetBrains Mono", monospace',
            minWidth: 46,
            textAlign: 'right',
          }}
        >
          {formatClock(positionSec)}
        </Typography>
        {/* Wave 20.38c — Native range input for the progress bar.
            MUI Slider's internal transition state doesn't keep up
            with 60 Hz controlled value updates during playback (the
            thumb stayed frozen at 0 until pause); native input has
            no such animation and reflects the value prop every
            render. Styled via CSS to match the rest of the app. */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <input
            type="range"
            min={0}
            max={Math.max(1, Math.floor(durationSec))}
            step={1}
            value={Math.min(Math.floor(positionSec), Math.floor(durationSec))}
            onChange={(e) => onSeek(Number(e.currentTarget.value))}
            style={{
              width: '100%',
              accentColor: colors.brand,
              cursor: 'pointer',
              background: 'transparent',
            }}
          />
        </Box>
        <Typography
          sx={{
            color: colors.textFaint,
            fontSize: '0.72rem',
            fontFamily: '"JetBrains Mono", monospace',
            minWidth: 46,
          }}
        >
          {formatClock(durationSec)}
        </Typography>
        <Stack direction="row" spacing={0.25}>
          {speedChoices.map((opt) => (
            <Box
              key={opt.value}
              onClick={() => onSpeedChange(opt.value)}
              sx={{
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                cursor: 'pointer',
                fontSize: '0.7rem',
                fontWeight: 800,
                color: speed === opt.value ? colors.bg : colors.textMuted,
                backgroundColor: speed === opt.value ? colors.brand : 'transparent',
                userSelect: 'none',
                transition: 'background-color 120ms',
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Stack>
        {onToggleFullscreen && (
          <IconButton
            size="small"
            onClick={onToggleFullscreen}
            sx={{ color: colors.textMuted }}
            aria-label={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

// ---------- clock hook ----------

/** Playback clock. Advances `positionSec` via requestAnimationFrame
 *  while `playing`, applying the speed multiplier. Clamps to
 *  [0, durationSec] and auto-pauses at the end. */
export function useReplayClock(
  durationSec: number,
  playing: boolean,
  speed: ReplaySpeed,
  onEnd?: () => void,
): {
  positionSec: number;
  setPositionSec: (v: number | ((prev: number) => number)) => void;
} {
  const [positionSec, setPositionSec] = React.useState(0);
  const positionRef = useRef(0);
  positionRef.current = positionSec;
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  useEffect(() => {
    if (!playing || durationSec <= 0) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      return;
    }
    lastFrameRef.current = performance.now();
    const tick = () => {
      const now = performance.now();
      const dtSec = (now - lastFrameRef.current) / 1000;
      lastFrameRef.current = now;
      const next = Math.min(durationSec, positionRef.current + dtSec * speed);
      setPositionSec(next);
      if (next >= durationSec) {
        onEndRef.current?.();
        rafRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [playing, speed, durationSec]);

  return { positionSec, setPositionSec };
}
